import {
  ALGERIA,
  COUNTRIES_USING_EURO,
  CURRENT_OFFER,
} from "~/config/constants";

/**
 * List prices, before the current promotional discount. `CURRENT_OFFER` is
 * applied on top by `getPricingFor(..., discounted)`, which is what the design's
 * struck-through "was" price and headline price represent.
 *
 * List values are chosen so the 20% launch offer lands on round headline
 * prices (e.g. 199 -> 159); re-check the rounding if `CURRENT_OFFER` changes.
 */
const PRICING_TABLE = {
  DZD: {
    groupCourses: { light: 4500, semiIntensive: 10000, intensive: 20000 },
    individualCourses: { light: 15000, semiIntensive: 25000, intensive: 40000 },
  },
  EURO: {
    groupCourses: { light: 149, semiIntensive: 249, intensive: 449 },
    individualCourses: { light: 449, semiIntensive: 749, intensive: 1249 },
  },
  USD: {
    groupCourses: { light: 149, semiIntensive: 249, intensive: 449 },
    individualCourses: { light: 449, semiIntensive: 749, intensive: 1249 },
  },
};

export type CourseType = "groupCourses" | "individualCourses";
export type CourseIntensity = "light" | "semiIntensive" | "intensive";

export const COURSE_TYPES: CourseType[] = ["groupCourses", "individualCourses"];
export const INTENSITIES: CourseIntensity[] = [
  "light",
  "semiIntensive",
  "intensive",
];

/** The tier rendered dark with the "Popular" badge. */
export const FEATURED_INTENSITY: CourseIntensity = "semiIntensive";

function getPricing(
  country: string,
  courseType: CourseType,
  courseIntensity: CourseIntensity,
): number {
  if (country === ALGERIA) {
    return PRICING_TABLE.DZD[courseType][courseIntensity];
  }

  if (COUNTRIES_USING_EURO.includes(country)) {
    return PRICING_TABLE.EURO[courseType][courseIntensity];
  }

  return PRICING_TABLE.USD[courseType][courseIntensity];
}

export function getPricingFor(
  country: string,
  courseType: CourseType,
  courseIntensity: CourseIntensity,
  locale: string,
  discounted = false,
): string {
  let pricing = getPricing(country, courseType, courseIntensity);
  if (discounted) {
    pricing = Math.round(pricing * (1 - Number(CURRENT_OFFER) / 100));
  }

  if (country === ALGERIA) {
    return locale === "ar" ? `${pricing} دج` : `DZD ${pricing}`;
  }

  if (COUNTRIES_USING_EURO.includes(country)) {
    return `€${pricing}`;
  }

  return `$${pricing}`;
}

export function getDiscountedPrice(
  country: string,
  courseType: CourseType,
  courseIntensity: CourseIntensity,
  locale: string,
): string {
  return getPricingFor(country, courseType, courseIntensity, locale, true);
}

/** Numeric price + ISO currency, for schema.org `Offer` markup. */
export function getOfferFor(
  country: string,
  courseType: CourseType,
  courseIntensity: CourseIntensity,
): { price: number; currency: string } {
  const listPrice = getPricing(country, courseType, courseIntensity);
  const price = Math.round(listPrice * (1 - Number(CURRENT_OFFER) / 100));

  if (country === ALGERIA) return { price, currency: "DZD" };
  if (COUNTRIES_USING_EURO.includes(country)) return { price, currency: "EUR" };
  return { price, currency: "USD" };
}
